// Floating damage numbers - object pooled for performance

import { Camera } from '@client/core/Camera';
import { t } from '@locales';
import { formatNumber } from '@shared/utils';

export type DamageNumberType =
  | 'physical' | 'gok' | 'od' | 'su' | 'karanlik'
  | 'crit' | 'miss' | 'dodge' | 'block'
  | 'heal' | 'poison_tick' | 'bleed_tick' | 'burn_tick'
  | 'xp' | 'level_up' | 'gold';

interface DamageNumber {
  active: boolean;
  worldX: number;
  worldY: number;
  value: number | string;
  type: DamageNumberType;
  timer: number;
  maxTimer: number;
  offsetX: number; // Spread offset to avoid overlap
  velocityY: number; // Upward float speed
  scale: number;
}

const POOL_SIZE = 60;

// Color map for damage types
const TYPE_COLORS: Record<DamageNumberType, string> = {
  physical: '#FFFFFF',
  gok: '#66CCFF',      // Sky/Lightning - blue
  od: '#FF4444',       // Fire - red
  su: '#44DDDD',       // Water - cyan
  karanlik: '#CC88FF',  // Dark/Chaos - purple
  crit: '#FFFF00',     // Yellow
  miss: '#888888',     // Grey
  dodge: '#AAAAAA',    // Light grey
  block: '#8888FF',    // Blue-grey
  heal: '#44FF44',     // Green
  poison_tick: '#88DD44', // Yellow-green
  bleed_tick: '#DD4444',  // Dark red
  burn_tick: '#FF8800',   // Orange
  xp: '#CC88FF',       // Purple
  level_up: '#FFD700', // Gold
  gold: '#FFD700',     // Gold
};

export class DamageNumberSystem {
  private pool: DamageNumber[] = [];
  private static instance: DamageNumberSystem | null = null;

  static getInstance(): DamageNumberSystem {
    if (!this.instance) this.instance = new DamageNumberSystem();
    return this.instance;
  }

  constructor() {
    // Pre-allocate pool
    for (let i = 0; i < POOL_SIZE; i++) {
      this.pool.push({
        active: false, worldX: 0, worldY: 0, value: 0,
        type: 'physical', timer: 0, maxTimer: 0.8,
        offsetX: 0, velocityY: -80, scale: 1,
      });
    }
  }

  spawn(worldX: number, worldY: number, value: number | string, type: DamageNumberType): void {
    // Find inactive slot
    let slot: DamageNumber | null = null;
    for (const num of this.pool) {
      if (!num.active) { slot = num; break; }
    }
    // If pool full, recycle oldest (smallest timer remaining)
    if (!slot) {
      slot = this.pool[0];
      for (const num of this.pool) {
        if (num.timer < slot.timer) slot = num;
      }
    }

    slot.active = true;
    slot.worldX = worldX;
    slot.worldY = worldY;
    slot.value = value;
    slot.type = type;

    // Spread offset to avoid overlap (-20 to +20)
    slot.offsetX = (Math.random() - 0.5) * 40;

    // Type-specific settings
    if (type === 'crit') {
      slot.maxTimer = 1.2;
      slot.velocityY = -100;
      slot.scale = 1.5;
    } else if (type === 'level_up') {
      slot.maxTimer = 2.0;
      slot.velocityY = -60;
      slot.scale = 2.0;
    } else if (type === 'xp' || type === 'gold') {
      slot.maxTimer = 0.6;
      slot.velocityY = -50;
      slot.scale = 0.7;
    } else if (type === 'miss' || type === 'dodge' || type === 'block') {
      slot.maxTimer = 0.6;
      slot.velocityY = -40;
      slot.scale = 0.8;
    } else if (type.endsWith('_tick')) {
      slot.maxTimer = 0.5;
      slot.velocityY = -30;
      slot.scale = 0.6;
    } else if (type === 'heal') {
      slot.maxTimer = 0.8;
      slot.velocityY = -60;
      slot.scale = 1.0;
    } else {
      slot.maxTimer = 0.8;
      slot.velocityY = -80;
      slot.scale = 1.0;
    }

    slot.timer = slot.maxTimer;
  }

  update(deltaTime: number): void {
    for (const num of this.pool) {
      if (!num.active) continue;
      num.timer -= deltaTime;
      num.worldY += num.velocityY * deltaTime;
      if (num.timer <= 0) num.active = false;
    }
  }

  /** Deactivate all pool entries (used on zone transitions to clear stale numbers) */
  clearAll(): void {
    for (const num of this.pool) {
      num.active = false;
    }
  }

  render(ctx: CanvasRenderingContext2D, camera: Camera): void {
    // Single save/restore for the entire batch
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    let lastFontSize = -1;

    for (const num of this.pool) {
      if (!num.active) continue;

      // Use allocation-free worldToScreenInPlace
      const screen = camera.worldToScreenInPlace(num.worldX, num.worldY);
      const sx = screen.x + num.offsetX;
      const sy = screen.y;

      const progress = 1 - (num.timer / num.maxTimer);
      // Fade out in last 30%
      const alpha = num.timer < num.maxTimer * 0.3
        ? num.timer / (num.maxTimer * 0.3)
        : 1.0;

      // Scale: pop in effect then shrink
      const popScale = progress < 0.1
        ? 0.5 + progress * 5 // 0.5 -> 1.0 in first 10%
        : 1.0;

      // Big damage bonus scale
      const bigDamageScale = (typeof num.value === 'number' && num.value >= 100) ? 1.15 : 1.0;
      const fontSize = Math.round(14 * num.scale * popScale * bigDamageScale * camera.zoom);
      const color = TYPE_COLORS[num.type];

      // Only change font when size changes (font parsing is expensive)
      if (fontSize !== lastFontSize) {
        ctx.font = `bold ${fontSize}px 'Arial Black', 'Arial', sans-serif`;
        lastFontSize = fontSize;
      }

      ctx.globalAlpha = alpha;

      // Shadow/outline for readability
      ctx.strokeStyle = 'rgba(0,0,0,0.8)';
      ctx.lineWidth = 3;

      let text: string;
      if (typeof num.value === 'string') {
        text = num.value;
      } else if (num.type === 'xp') {
        text = `+${formatNumber(num.value)} XP`;
      } else if (num.type === 'gold') {
        text = `+${formatNumber(num.value)}`;
      } else if (num.type === 'heal') {
        text = `+${formatNumber(num.value)}`;
      } else if (num.type === 'miss') {
        text = t('combat.miss');
      } else if (num.type === 'dodge') {
        text = t('combat.dodged');
      } else if (num.type === 'block') {
        text = t('combat.blocked');
      } else if (num.type === 'level_up') {
        text = t('combat.levelUp');
      } else if (num.type === 'crit') {
        text = `${formatNumber(num.value)}!`;
      } else {
        text = `${formatNumber(num.value)}`;
      }

      ctx.strokeText(text, sx, sy);
      ctx.fillStyle = color;
      ctx.fillText(text, sx, sy);

      // Crit extra glow
      if (num.type === 'crit') {
        ctx.shadowColor = '#FFFF00';
        ctx.shadowBlur = 20;
        ctx.fillText(text, sx, sy);
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }

      // Heal glow
      if (num.type === 'heal') {
        ctx.shadowColor = '#44FF44';
        ctx.shadowBlur = 12;
        ctx.fillText(text, sx, sy);
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }

      // Level up glow
      if (num.type === 'level_up') {
        ctx.shadowColor = '#FFD700';
        ctx.shadowBlur = 16;
        ctx.fillText(text, sx, sy);
        ctx.shadowBlur = 0;
        ctx.shadowColor = 'transparent';
      }
    }

    ctx.restore();
  }
}

// Singleton accessor
let _damageNumbers: DamageNumberSystem | null = null;
export function getDamageNumbers(): DamageNumberSystem {
  if (!_damageNumbers) _damageNumbers = new DamageNumberSystem();
  return _damageNumbers;
}
